/**
 * Theme: `paper` for daylight, `night` for the small hours.
 *
 * The mode is stamped on `<html data-theme>` so CSS owns the styling, and also
 * exposed through context because the plot renderer picks colours in JS.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { ThemeMode } from "./plot/palette.ts";

const STORAGE_KEY = "szero.theme";

interface ThemeContextValue {
  readonly mode: ThemeMode;
  readonly toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  mode: "paper",
  toggle: () => {},
});

function readInitialMode(): ThemeMode {
  if (typeof window === "undefined") return "paper";

  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "paper" || stored === "night") return stored;

  return window.matchMedia?.("(prefers-color-scheme: dark)").matches
    ? "night"
    : "paper";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<ThemeMode>(readInitialMode);

  useEffect(() => {
    document.documentElement.dataset.theme = mode;
    window.localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  const toggle = useCallback(() => {
    setMode((current) => (current === "paper" ? "night" : "paper"));
  }, []);

  const value = useMemo(() => ({ mode, toggle }), [mode, toggle]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = (): ThemeContextValue => useContext(ThemeContext);
